import { uniqueId, isEmpty } from 'lodash';
import validator from 'validator';
import { decodeRecArea, encodeRecArea, encodeRecAreaActivity, encodeRecAreaAddress } from './recArea';
import { decodeFacility, encodeFacility, encodeFacilityActivity, encodeFacilityAddress } from './facility';

export const assetTypeDetails = (type) => {
  switch (type) {
    case 'facility':
    case 'facilities':
      return {
        name: 'Facility',
        longName: 'Facility',
        singular: 'facility',
        plural: 'facilities',
        activity: 'facilityactivity',
        activities: 'facilityactivities',
        address: 'facilityaddreess',
        addresses: 'facilityaddresses',
        link: 'facilitylink',
        links: 'facilitylinks',
        media: 'facilitymedia',
        event: 'facilityevent',
        events: 'facilityevents'
      };
    default:
      return {
        name: 'Rec Area',
        longName: 'Recreation Area',
        singular: 'recarea',
        plural: 'recareas',
        activity: 'recareaactivity',
        activities: 'recareaactivities',
        address: 'recareaaddreess',
        addresses: 'recareaaddresses',
        link: 'recarealink',
        links: 'recarealinks',
        media: 'recareamedia',
        event: 'recareaevent',
        events: 'recareaevents'
      };
  }
};

const orgKeys = {
  OrgID: 'id',
  OrgName: 'name',
  OrgImageURL: 'imageUrl',
  OrgURLText: 'urlText',
  OrgURLAddress: 'urlAddress',
  OrgType: 'type',
  OrgAbbrevName: 'abbrevName',
  OrgJurisdictionType: 'jurisdictionType',
  OrgParentID: 'parentId',
  LastUpdatedDate: 'lastUpdatedDate'
};

function decodeOrganizations(records) {
  return records.map((data) => {
    const org = { _key: uniqueId('org-') };
    Object.keys(orgKeys).forEach((key) => org[orgKeys[key]] = data[key]);
    return org;
  });
}

// Asset Links
const decodeLinkKeys = {
  EntityLinkID: 'id',
  LinkType: 'type',
  EntityID: 'entityId',
  EntityType: 'entityType',
  Title: 'title',
  Description: 'description',
  URL: 'url'
};
const encodeLinkKeys = {
  id: 'entityLinkID',
  type: 'linkType',
  title: 'title',
  description: 'description',
  url: 'URL'
};

function decodeLinks(records) {
  return records.map((record) => {
    const link = { _key: uniqueId('link-') };
    Object.keys(decodeLinkKeys).forEach((key) => link[decodeLinkKeys[key]] = record[key]);
    return link;
  });
}

export const encodeLink = (record) => {
  const link = {};
  Object.keys(encodeLinkKeys).forEach((key) => link[encodeLinkKeys[key]] = record[key]);
  return link;
};

// Asset Events
const decodeEventKeys = {
  EventID: 'id',
  EntityID: 'entityId',
  EntityType: 'entityType',
  EventName: 'name',
  Description: 'description',
  EventTypeDescription: 'typeDescription',
  EventFeeDescription: 'feeDescription',
  EventFrequencyRateDescription: 'frequencyRateDescription',
  EventScopeDescription: 'scopeDescription',
  EventAgeGroup: 'ageGroup',
  EventRegistrationRequired: 'registrationRequired',
  EventADAAccess: 'adaAccess',
  EventComments: 'comments',
  EventEmail: 'email',
  EventURLAddress: 'url',
  EventURLText: 'urlText',
  EventStartDate: 'startDate',
  EventEndDate: 'endDate',
  SponsorName: 'sponsorName',
  SponsorClassType: 'sponsorClassType',
  SponsorPhone: 'sponsorPhone',
  SponsorEmail: 'sponsorEmail',
  SponsorURLAddress: 'sponsorUrl',
  SponsorURLText: 'sponsorUrlText'
};

const encodeEventKeys = {
  id: 'eventID',
  entityId: 'entityID',
  entityType: 'entityType',
  name: 'eventName',
  description: 'eventDescription',
  typeDescription: 'eventTypeDescription',
  feeDescription: 'eventFeeDescription',
  frequencyRateDescription: 'eventFrequencyRateDescription',
  scopeDescription: 'eventScopeDescription',
  ageGroup: 'eventAgeGroup',
  registrationRequired: 'eventRegistrationRequired',
  adaAccess: 'eventADAAccess',
  comments: 'eventComments',
  email: 'eventEmail',
  url: 'eventURLAddress',
  urlText: 'eventURLText',
  startDate: 'eventStartDate',
  endDate: 'eventEndDate',
  sponsorName: 'sponsorName',
  sponsorClassType: 'sponsorClassType',
  sponsorPhone: 'sponsorPhone',
  sponsorEmail: 'sponsorEmail',
  sponsorUrl: 'sponsorURLAddress',
  sponsorUrlText: 'sponsorURLText'
};

export function decodeEvents(records) {
  return records.map((record) => {
    const event = { _key: uniqueId('event-') };
    Object.keys(decodeEventKeys).forEach((key) => event[decodeEventKeys[key]] = record[key]);
    return event;
  });
}

export function encodeEvent(record) {
  const event = {};
  Object.keys(encodeEventKeys).forEach((key) => event[encodeEventKeys[key]] = record[key]);
  return event;
}

// Asset Media
const decodeMediaKeys = {
  EntityMediaID: 'id',
  MediaType: 'type',
  EntityID: 'entityId',
  EntityType: 'entityType',
  Title: 'title',
  Subtitle: 'subtitle',
  Description: 'description',
  EmbedCode: 'embedCode',
  Height: 'height',
  Width: 'width',
  URL: 'url',
  Credits: 'credits',
  IsPrimary: 'isPrimary',
  IsPreview: 'isPreview',
  IsGallery: 'isGallery'
};
const encodeMediaKeys = {
  id: 'entityMediaID',
  type: 'mediaType',
  title: 'title',
  subtitle: 'subtitle',
  description: 'description',
  embedCode: 'embedcode',
  height: 'height',
  width: 'width',
  url: 'URL',
  image: 'image',
  credits: 'credits',
  isPrimary: 'isPrimary',
  isPreview: 'isPreview',
  isGallery: 'isGallery'
};

function decodeMedia(records) {
  return records.map((record) => {
    const media = { _key: uniqueId('media-') };
    Object.keys(decodeMediaKeys).forEach((key) => media[decodeMediaKeys[key]] = record[key]);
    return media;
  });
}

export const encodeMedia = (record) => {
  if (record.image) {
    const formData = new FormData();
    Object.keys(encodeMediaKeys).forEach((key) => {
      if (record[key] !== null && record[key] !== undefined ) {
        formData.append(encodeMediaKeys[key], record[key]);
      }
    });
    // return the form data and options for multipart form
    return [formData, { 'Content-Type': 'multipart/form-data' }];
  }
  // encode normally as JSON
  const media = {};
  Object.keys(encodeMediaKeys).forEach((key) => media[encodeMediaKeys[key]] = record[key]);
  return [media, {}];
};

function validateMediaImage(media) {
  if (!media.url && !media.image) {
    return [
      {
        field: 'url',
        messages: ['Must have either an image file or a URL']
      },
      {
        field: 'image',
        messages: ['Must have either an image file or a URL']
      }
    ];
  }
  return null;
}

export const decodeAsset = (record) => {
  let asset;

  if (record.hasOwnProperty('RecAreaID')) {
    asset = decodeRecArea(record);
  }
  else {
    asset = decodeFacility(record);
  }

  asset.eventsToLoad = record.EVENT && record.EVENT.length > 0;

  asset.organizations = decodeOrganizations(record.ORGANIZATION);
  asset.links = decodeLinks(record.LINK);
  asset.media = decodeMedia(record.MEDIA);

  return asset;
};

export const buildCall = (change, assetId) => {
  const type = change.type;
  const ref = change.ref;
  let options = {};
  let data = {};
  let verb = 'PUT';
  let url = '';
  let descriptionNoun = '';

  switch (type) {
    case 'recarea':
    case 'recareas':
      descriptionNoun = 'Rec Area Body';
      data = encodeRecArea({ ...change.ids, ...change.changes });
      if (change._delete) {
        verb = 'DELETE';
        url = `/recareas/${data.recAreaID}`;
      }
      else if (!data.recAreaID) {
        verb = 'POST';
        url = `/recareas`;
      }
      else {
        url = `/recareas/${data.recAreaID}`;
      }
      break;
    case 'facility':
    case 'facilities':
      descriptionNoun = 'Facility Body';
      data = encodeFacility({ ...change.ids, ...change.changes });
      if (change._delete) {
        verb = 'DELETE';
        url = `/facilities/${data.facilityID}`;
      }
      else if (!data.facilityID) {
        verb = 'POST';
        url = `/facilities`;
      }
      else {
        url = `/facilities/${data.facilityID}`;
      }
      break;
    case 'recareaactivity':
    case 'recareaactivities':
      descriptionNoun = 'Rec Area Activities';
      data = encodeRecAreaActivity({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/recareas/${assetId}/activities/${data.activityID}`;
      }
      else {
        verb = 'POST';
        url = `/recareas/${assetId}/activities`;
      }
      break;
    case 'facilityactivity':
    case 'facilityactivities':
      descriptionNoun = 'Facility Activities';
      data = encodeFacilityActivity({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/facilities/${assetId}/activities/${data.activityID}`;
      }
      else {
        verb = 'POST';
        url = `/facilities/${assetId}/activities`;
      }
      break;
    case 'recareaaddress':
    case 'recareaaddresses':
      descriptionNoun = 'Rec Area Address';
      data = encodeRecAreaAddress({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/recareas/${assetId}/recareaaddresses/${data.recAreaAddressID}`;
      }
      else if (!data.recAreaAddressID) {
        verb = 'POST';
        url = `/recareas/${assetId}/recareaaddresses`;
      }
      else {
        url = `/recareas/${assetId}/recareaaddresses/${data.recAreaAddressID}`;
      }
      break;
    case 'facilityaddress':
    case 'facilityaddresses':
      descriptionNoun = 'Facility Address';
      data = encodeFacilityAddress({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/facilities/${assetId}/facilityaddresses/${data.facilityAddressID}`;
      }
      else if (!data.facilityAddressID) {
        verb = 'POST';
        url = `/facilities/${assetId}/facilityaddresses`;
      }
      else {
        url = `/facilities/${assetId}/facilityaddresses/${data.facilityAddressID}`;
      }
      break;
    case 'recarealink':
    case 'recarealinks':
      descriptionNoun = 'Rec Area Link';
      data = encodeLink({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/recareas/${assetId}/links/${data.entityLinkID}`;
      }
      else if (!data.entityLinkID) {
        verb = 'POST';
        url = `/recareas/${assetId}/links`;
      }
      else {
        url = `/recareas/${assetId}/links/${data.entityLinkID}`;
      }
      break;
    case 'facilitylink':
    case 'facilitylinks':
      descriptionNoun = 'Facility Link';
      data = encodeLink({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/facilities/${assetId}/links/${data.entityLinkID}`;
      }
      else if (!data.entityLinkID) {
        verb = 'POST';
        url = `/facilities/${assetId}/links`;
      }
      else {
        url = `/facilities/${assetId}/links/${data.entityLinkID}`;
      }
      break;
    case 'recareamedia': {
      descriptionNoun = 'Rec Area Image';
      const record = { ...change.ids, ...change.changes };
      [data, options] = encodeMedia(record);
      const entityMediaID = isEmpty(options) ? data.entityMediaID : data.get('entityMediaID');
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/recareas/${assetId}/media/${entityMediaID}`;
      }
      else if (!entityMediaID) {
        const validationErrors = validateMediaImage(record);
        if (validationErrors !== null) {
          throw validationErrors;
        }
        verb = 'POST';
        url = `/recareas/${assetId}/media`;
      }
      else {
        url = `/recareas/${assetId}/media/${entityMediaID}`;
      }
      break;
    }
    case 'facilitymedia': {
      descriptionNoun = 'Facility Image';
      const record = { ...change.ids, ...change.changes };
      [data, options] = encodeMedia(record);
      const entityMediaID = isEmpty(options) ? data.entityMediaID : data.get('entityMediaID');
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/facilities/${assetId}/media/${entityMediaID}`;
      }
      else if (!entityMediaID) {
        const validationErrors = validateMediaImage(record);
        if (validationErrors !== null) {
          throw validationErrors;
        }
        verb = 'POST';
        url = `/facilities/${assetId}/media`;
      }
      else {
        url = `/facilities/${assetId}/media/${entityMediaID}`;
      }
      break;
    }
    case 'recareaevent':
    case 'recareaevents':
      descriptionNoun = 'Rec Area Event';
      data = encodeEvent({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/recareas/${assetId}/events/${data.eventID}`;
      }
      else if (!data.eventID) {
        verb = 'POST';
        url = `/recareas/${assetId}/events`;
      }
      else {
        url = `/recareas/${assetId}/events/${data.eventID}`;
      }
      break;
    case 'facilityevent':
    case 'facilityevents':
      descriptionNoun = 'Facility Event';
      data = encodeEvent({ ...change.ids, ...change.changes });
      if (change.changes && change.changes._delete) {
        verb = 'DELETE';
        url = `/facilities/${assetId}/events/${data.eventID}`;
      }
      else if (!data.eventID) {
        verb = 'POST';
        url = `/facilities/${assetId}/events`;
      }
      else {
        url = `/facilities/${assetId}/events/${data.eventID}`;
      }
      break;

    default:
    // no
  }

  let descriptionVerb = '';
  switch (verb) {
    case 'POST':
      descriptionVerb = 'saving';
      break;
    case 'DELETE':
      descriptionVerb = 'deleting';
      break;
    case 'PUT':
    default:
      descriptionVerb = 'creating';
  }

  return { type, data, verb, url, description: `${descriptionVerb} ${descriptionNoun}`, ref, options };
};

export const decodePostResponseId = (type, response) => {
  switch (type) {
    case 'recarea':
    case 'recareas':
      return response.RecAreaID;
    case 'facility':
    case 'facilities':
      return response.FacilityID;
    case 'recareaactivity':
    case 'recareaactivities':
    case 'facilityactivity':
    case 'facilityactivities':
      return response.ActivityID;
    case 'recareaaddress':
    case 'recareaaddresses':
      return response.RecAreaAddressID;
    case 'facilityaddress':
    case 'facilityaddresses':
      return response.FacilityAddressID;
    case 'recarealink':
    case 'recarealinks':
    case 'facilitylink':
    case 'facilitylinks':
      return response.LinkID;
    case 'recareamedia':
    case 'facilitymedia':
      return response.MediaID;
    case 'recareaevent':
    case 'recareaevents':
    case 'facilityevent':
    case 'facilityevents':
      return response.EventID;

    default:
      // no
      return null;
  }
};

function requiredField(value) {
  if (!value) {
    return ['Required Field'];
  }
  return null;
}

export function emailField(value) {
  if (!validator.isEmail(value)) {
    return ['Not a valid email address.'];
  }
  return null;
}

function validateRecArea(field, value) {
  switch (field) {
    case 'name':
    case 'description':
    case 'directions':
      return requiredField(value);
    default:
      return null;
  }
}

function validateFacility(field, value) {
  switch (field) {
    case 'name':
    case 'description':
    case 'directions':
      return requiredField(value);
    default:
      return null;
  }
}

function validateActivity() {
  return null;
}

function validateAddress(field, value) {
  switch (field) {
    case 'stateCode':
    case 'countryCode':
      return requiredField(value);
    default:
      return null;
  }
}

function validateLinks(field, value) {
  switch (field) {
    case 'url':
    case 'title':
      return requiredField(value);
    default:
      return null;
  }
}

function validateMedia(field, value) {
  switch (field) {
    case 'title':
      return requiredField(value);
    default:
      return null;
  }
}

function validateEvent(field, value) {
  switch (field) {
    case 'name':
      return requiredField(value);
    default:
      return null;
  }
}

export const doValidate = (type, field, value) => {
  switch (type) {
    case 'recarea':
    case 'recareas':
      return validateRecArea(field, value);
    case 'facility':
    case 'facilities':
      return validateFacility(field, value);
    case 'recareaactivity':
    case 'recareaactivities':
    case 'facilityactivity':
    case 'facilityactivities':
      return validateActivity(field, value);
    case 'recareaaddress':
    case 'recareaaddresses':
    case 'facilityaddress':
    case 'facilityaddresses':
      return validateAddress(field, value);
    case 'recarealink':
    case 'recarealinks':
    case 'facilitylink':
    case 'facilitylinks':
      return validateLinks(field, value);
    case 'recareamedia':
    case 'facilitymedia':
      return validateMedia(field, value);
    case 'recareaevent':
    case 'recareaevents':
    case 'facilityevent':
    case 'facilityevents':
      return validateEvent(field, value);

    default:
      return null;
  }
};

